/**
 * ADR-0047: the opencode runtime launcher/supervisor - the manager-side
 * analogue of `persistent-session.ts`, but for the `opencode` framework.
 *
 * Where Claude Code is an interactive TUI the manager keeps alive in a tmux
 * pane and drives with `tmux send-keys` (scraping `pane.log` for output),
 * opencode exposes a real headless HTTP server (`opencode serve`). So this
 * module supervises a per-agent `opencode serve` PROCESS and drives it over
 * HTTP:
 *
 *   start  → spawn `opencode serve` (one per agent, its own port + password)
 *   health → `tmux has-session` (process liveness) + an HTTP readiness probe
 *   inject → POST a framed inbound turn via OpencodeInboundBridge (durable admit)
 *   stop   → kill the tmux session
 *
 * No send-keys, no pty, no pane scraping. The process still runs inside a tmux
 * session so the rest of the manager's supervision surface (tmux ls, the boot
 * reaper, diagnostics) sees opencode agents the same way it sees Claude Code
 * ones - the session name is prefixed `agt-oc-` to keep the two namespaces
 * distinct.
 *
 * Isolation (ADR-0047): the decision is container-per-agent, reusing the
 * ADR-0014 Docker + squid boundary. That launcher does not exist yet, so this
 * first slice runs `opencode serve` as a bare per-agent process and REFUSES to
 * spawn when the host asks for isolation (`config.isolated`) rather than
 * silently running unisolated on a multi-tenant host - the T5 cross-agent
 * boundary must not regress. Single-agent / dedicated hosts (no isolation) run
 * bare today; the container path is the tracked follow-up.
 */

import { spawn, execSync } from 'node:child_process';
import { createServer } from 'node:net';
import { randomBytes } from 'node:crypto';
import { existsSync, mkdirSync, readFileSync, writeFileSync, rmSync, chmodSync } from 'node:fs';
import { homedir, userInfo } from 'node:os';
import { join, dirname } from 'node:path';

import {
  HttpOpencodeClient,
  OpencodeInboundBridge,
  parseOpencodeModelRef,
  buildOpencodeTranscript,
  emptyOpencodeTranscript,
  type InboundMessage,
  type InboundResult,
  type SenderGate,
  type OpencodeModelRef,
  type SessionSummary,
} from '@augmented/core/provisioning/frameworks/opencode/index.js';
import { redactForDiskLog } from './manager/runtime.js';
import { TurnOutcomeTracker, type TurnOutcome, type TurnHealth } from './turn-outcome-tracker.js';
import { OpencodeActivityTracker } from './opencode-activity-tracker.js';

/** Binary that serves the headless API. Overridable for non-PATH installs. */
const OPENCODE_BIN = process.env['AGT_OPENCODE_BIN']?.trim() || 'opencode';

/**
 * Max wall-clock for one turn. ENG-8032 used this to SIGKILL a wedged `opencode
 * run` subprocess; ENG-9096 turned it into the HTTP client's request timeout,
 * since `POST /session/{id}/message` now blocks for the whole turn. Same budget,
 * same reason it is generous: a cold turn re-discovers the augmented MCP bundle
 * (~110 tools) before the model responds, and the client's own 120s default
 * would cut that short.
 *
 * The env var keeps its name so operators who already set it are unaffected.
 *
 * NOTE: hitting this no longer cancels anything. Measured (ENG-9096) — opencode
 * runs the turn to completion and persists the reply even after the client goes
 * away, so a timeout loses the REPLY, not the turn. That is why the bridge does
 * not redeliver on it.
 */
const OPENCODE_RUN_TIMEOUT_MS =
  Number(process.env['AGT_OPENCODE_RUN_TIMEOUT_MS']) || 180_000;

/** Basic-auth username the client + server agree on (opencode's default). */
const SERVER_USERNAME = 'opencode';

export interface OpencodeSessionConfig {
  codeName: string;
  agentId: string;
  /**
   * The agent's project dir - holds `opencode.json`, `AGENTS.md` and
   * `CHARTER.md` (written by the opencode adapter's provisioning path). The
   * server is spawned with this as its cwd so it loads that config + identity.
   */
  projectDir: string;
  /**
   * Env forwarded into the `opencode serve` process. This is where the PROVIDER
   * KEY lives (ANTHROPIC_API_KEY / OPENROUTER_API_KEY / VERCEL_AI_GATEWAY_API_KEY
   * / ...), plus the AGT_* wiring the bundled MCP server reads. Assembled by the
   * manager from the same trusted refresh data the Claude Code path uses; no
   * Anthropic subscription credential is mounted (the ADR-0046 driver).
   */
  serveEnv?: Record<string, string | undefined>;
  /**
   * ENG-5051 parity: per-session run UUID, exported so the bundled MCP's
   * `{env:AGT_RUN_ID}` substitution resolves to a real run. Optional.
   */
  runId?: string | null;
  /** IANA timezone for the serve process (parity with the Claude Code path). */
  agentTimezone?: string | null;
  /**
   * True when the host wants Docker isolation for this agent. Until the
   * container launcher lands (ADR-0047), a true value means "refuse to spawn"
   * rather than run unisolated on a multi-tenant host.
   */
  isolated?: boolean;
  /**
   * Sender/peer gate composed by the manager from the real classifiers
   * (slack-inbound-filter, *-peer-classifier). Default: admit everything (only
   * safe on a trusted single-tenant host; the manager should always pass one).
   */
  gate?: SenderGate;
  log: (msg: string) => void;
}

export interface OpencodeSession {
  codeName: string;
  startedAt: number | null;
  restartCount: number;
  status: 'starting' | 'running' | 'stopped' | 'crashed';
  /** Port the `opencode serve` process is bound to (loopback only). */
  port: number | null;
  /** Basic-auth password minted for this server instance. */
  password: string | null;
  /** Tail of the pane log captured when the session last transitioned to crashed. */
  lastFailureTail: string | null;
  /**
   * ENG-7931: the {providerID,id} model parsed from the provisioned opencode.json
   * at spawn, passed to the bridge for every session-create + prompt. The serve
   * does not apply the config default model to API sessions, so without this an
   * agent silently runs a free Zen model. Null when the config is missing/unparseable.
   */
  model: OpencodeModelRef | null;
  /**
   * ENG-8032: the serve's project dir (= the provision dir; where opencode.json
   * + the MCP config live). Passed to `opencode run --dir` so the run turn loads
   * the SAME project config + MCP servers the serve did.
   */
  projectDir: string | null;
}

/** codeName → live session state. */
const sessions = new Map<string, OpencodeSession>();
/**
 * ENG-7996: codeName → the manager's logger, captured at spawn. The inject path
 * is driven by channel ingests that carry no logger of their own, so the
 * turn-health warning has no other way to reach `manager.log`. Populated by
 * `startOpencodeSession` and cleared by `stopOpencodeSession`; a missing entry
 * simply means the warning is skipped (tracking still happens).
 */
const loggers = new Map<string, (msg: string) => void>();
/**
 * ENG-7996: per-agent TURN-COMPLETION health, the signal that is distinct from
 * `tmuxAlive`. See turn-outcome-tracker.ts for why process liveness alone let a
 * wedged agent report healthy.
 */
const turnOutcomeTracker = new TurnOutcomeTracker();
/**
 * ENG-8090: per-agent OCCUPANCY — the busy/idle substrate, deliberately separate
 * from turn health above. See opencode-activity-tracker.ts for why the liveness
 * and utilisation consumers cannot share one signal.
 */
const activityTracker = new OpencodeActivityTracker();
/**
 * codeName → inbound bridge. The bridge holds the conversationKey→sessionID map
 * (one opencode session per thread/DM), so it must persist across inbound calls
 * and be rebuilt only when the server's port/password change (a respawn).
 */
const bridges = new Map<string, { port: number; password: string; bridge: OpencodeInboundBridge }>();

/** tmux session name. Prefixed to stay distinct from Claude Code's `agt-<code>`. */
export function opencodeTmuxSession(codeName: string): string {
  return `agt-oc-${codeName}`;
}

/** Where we redirect the serve process's pane output, for post-crash recovery. */
export function opencodePaneLogPath(codeName: string): string {
  return join(homedir(), '.augmented', codeName, 'opencode-serve.log');
}

/**
 * ENG-7927: the redacted structured-transcript snapshot the manager writes while
 * a serve is running, for the admin Live View (the API SSM-reads this file; the
 * serve password never touches disk). Under the agent dir so the pane-log reaper
 * and diagnostics tooling see it alongside opencode-serve.log.
 */
export function opencodeTranscriptPath(codeName: string): string {
  // Sits in the same per-agent dir as the serve log. Derived from
  // opencodePaneLogPath rather than re-joining '.augmented'/codeName so the
  // per-agent-dir source of truth stays single (ADR-0049).
  return join(dirname(opencodePaneLogPath(codeName)), 'opencode-transcript.json');
}

/** How often the transcript refresher polls the serve while it is running. */
const TRANSCRIPT_REFRESH_MS = 3_000;
/** Cap the snapshot so the on-disk file (and the SSM read) stay bounded. */
const TRANSCRIPT_MAX_MESSAGES = 100;
/** codeName → live transcript-refresh interval (one per running serve). */
const transcriptTimers = new Map<string, NodeJS.Timeout>();

/** The most-recently-updated session, so the viewer follows the active conversation. */
function pickNewestSession(sessions: SessionSummary[]): SessionSummary | null {
  let newest: SessionSummary | null = null;
  let newestT = Number.NEGATIVE_INFINITY;
  for (const s of sessions) {
    const t = s.updated ?? s.created ?? 0;
    if (t >= newestT) {
      newestT = t;
      newest = s;
    }
  }
  return newest;
}

/**
 * Read the running serve's most-recent session over the loopback API, build a
 * REDACTED transcript, and write it to opencode-transcript.json (0600). Best
 * effort and non-throwing: a busy/unready serve just skips this tick and the
 * previous snapshot stands. Redaction uses the same `redactForDiskLog` scrub the
 * manager applies to its own logs, and raw tool inputs are dropped by the core
 * builder - so no secret (or the serve password) is ever written here.
 */
async function refreshOpencodeTranscript(codeName: string): Promise<void> {
  const session = sessions.get(codeName);
  if (!session || session.status !== 'running' || !session.port || !session.password) return;
  const client = new HttpOpencodeClient({
    baseUrl: baseUrlFor(session.port),
    password: session.password,
    requestTimeoutMs: 8_000,
  });
  let transcript;
  try {
    const newest = pickNewestSession(await client.listSessions());
    transcript = newest
      ? buildOpencodeTranscript({
          sessionId: newest.id,
          sessionTitle: newest.title ?? null,
          messages: await client.getStructuredMessages(newest.id),
          capturedAt: Date.now(),
          redact: redactForDiskLog,
          maxMessages: TRANSCRIPT_MAX_MESSAGES,
        })
      : emptyOpencodeTranscript(Date.now());
  } catch {
    return; // serve busy / not yet bound this tick - keep the last snapshot
  }
  try {
    const target = opencodeTranscriptPath(codeName);
    mkdirSync(dirname(target), { recursive: true });
    writeFileSync(target, JSON.stringify(transcript), { mode: 0o600 });
    // writeFileSync's mode only applies on create; enforce 0600 on overwrite too.
    try { chmodSync(target, 0o600); } catch { /* best-effort */ }
  } catch {
    /* best-effort: a failed write just means a stale snapshot, never a crash */
  }
}

/** Start (idempotent) the per-agent transcript refresher for a running serve. */
function startTranscriptRefresher(codeName: string): void {
  if (transcriptTimers.has(codeName)) return;
  const timer = setInterval(() => {
    void refreshOpencodeTranscript(codeName);
  }, TRANSCRIPT_REFRESH_MS);
  // Never keep the manager process alive on this timer alone.
  timer.unref?.();
  transcriptTimers.set(codeName, timer);
  // Write an immediate snapshot so the file exists promptly after a spawn.
  void refreshOpencodeTranscript(codeName);
}

/** Stop the transcript refresher (respawn / teardown). Leaves the last snapshot on disk. */
function stopTranscriptRefresher(codeName: string): void {
  const timer = transcriptTimers.get(codeName);
  if (timer) {
    clearInterval(timer);
    transcriptTimers.delete(codeName);
  }
}

/**
 * ENG-7928: read the `model` currently written into the agent's provisioned
 * opencode.json (the file `opencode serve` reads at boot). The manager compares
 * this to the freshly-resolved model to decide whether a model change has
 * actually landed on disk before hot-reloading the serve. Returns null if the
 * file is missing/unreadable/malformed (treated as "not yet provisioned").
 *
 * Takes the agent dir (from the canonical `getAgentDir` seam, ADR-0049) rather
 * than re-joining `.augmented`/codename here.
 */
export function readProvisionedOpencodeModel(agentDir: string): string | null {
  return readOpencodeModelString(join(agentDir, 'provision', 'opencode.json'));
}

/** Read the raw `model` string from an opencode.json at `<dir>/opencode.json`. */
function readOpencodeModelString(configPath: string): string | null {
  try {
    const parsed = JSON.parse(readFileSync(configPath, 'utf-8')) as { model?: unknown };
    return typeof parsed.model === 'string' ? parsed.model : null;
  } catch {
    return null;
  }
}

/**
 * ENG-7931: parse the `{providerID,id}` model selector from the opencode.json in
 * a serve's project/config dir (where `config.projectDir` points). Returns null
 * if absent/unparseable - the bridge then omits the model and the serve uses its
 * own default (a free Zen model), which is the pre-fix behaviour.
 */
export function readOpencodeModelFromConfigDir(configDir: string): OpencodeModelRef | null {
  return parseOpencodeModelRef(readOpencodeModelString(join(configDir, 'opencode.json')));
}

/**
 * Replace every `{env:NAME}` placeholder in a string with `env[NAME]` when that
 * var is a non-empty string; leave the placeholder intact otherwise. Pure, so
 * the substitution rule is unit-tested independently of the filesystem.
 */
export function materializeEnvPlaceholders(
  raw: string,
  env: Record<string, string | undefined>,
): string {
  return raw.replace(/\{env:([A-Za-z_][A-Za-z0-9_]*)\}/g, (whole, name: string) => {
    const v = env[name];
    return typeof v === 'string' && v.length > 0 ? v : whole;
  });
}

/** Filename opencode loads AFTER (and merges over) opencode.json in a project. */
const MATERIALIZED_CONFIG_BASENAME = 'opencode.jsonc';

/**
 * The GLOBAL opencode config path the serve loads at boot - `$XDG_CONFIG_HOME`
 * if set, else `~/.config`, then `opencode/opencode.json`. Derived from the SERVE
 * env (HOME/XDG_CONFIG_HOME) so it matches exactly what the spawned process reads.
 * Pure/exported for unit testing.
 */
export function opencodeGlobalConfigPath(serveEnv: NodeJS.ProcessEnv): string {
  const xdg = serveEnv['XDG_CONFIG_HOME']?.trim();
  const base = xdg && xdg.length > 0 ? xdg : join((serveEnv['HOME']?.trim()) || homedir(), '.config');
  return join(base, 'opencode', 'opencode.json');
}

/**
 * ENG-7956: extract the `{ $schema, mcp }` the serve needs at the GLOBAL level
 * from a materialized opencode config. Returns null when the config has no MCP
 * servers (nothing to deliver globally). Pure/exported for unit testing.
 */
export function buildGlobalMcpConfig(materializedConfig: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(materializedConfig);
  } catch {
    return null;
  }
  // Guard non-object JSON (`null`, `true`, `42`, `"str"`) - all valid JSON but no mcp.
  if (typeof parsed !== 'object' || parsed === null) return null;
  const cfg = parsed as { $schema?: string; mcp?: Record<string, unknown> };
  if (!cfg.mcp || Object.keys(cfg.mcp).length === 0) return null;
  return JSON.stringify(
    { $schema: cfg.$schema ?? 'https://opencode.ai/config.json', mcp: cfg.mcp },
    null,
    2,
  );
}

/** Write a config file 0600 (mode only applies on create, so chmod on overwrite too). */
function writeConfigFile600(target: string, content: string, codeName: string, log: (m: string) => void): boolean {
  try {
    mkdirSync(dirname(target), { recursive: true });
    writeFileSync(target, content, { mode: 0o600 });
    try { chmodSync(target, 0o600); } catch { /* best-effort */ }
    return true;
  } catch (err) {
    log(`[opencode-session] failed to write ${target} for '${codeName}': ${(err as Error).message}`);
    return false;
  }
}

/**
 * ENG-7931 + ENG-7956: make the serve run on the provisioned model/provider AND
 * load the provisioned MCP servers.
 *
 * opencode reads the PROJECT config from its cwd - here the provision dir - and a
 * project config OVERRIDES the global (`$XDG_CONFIG_HOME/opencode/`) one for
 * model/provider/permission (resolved per session). It also does NOT substitute
 * `{env:NAME}` in a custom provider's `options.apiKey` (validated: it forwards the
 * literal string and xAI 401s "Incorrect API key"), so the drift-checked provision
 * `opencode.json` - which MUST stay a secret-free `{env:…}` template - can't be the
 * file the serve auths from. So we write a MATERIALIZED sibling `opencode.jsonc`
 * next to the template (resolving every `{env:NAME}` from the spawn-scoped serve
 * env); the .jsonc wins at load time. Ownership stays clean: the provision loop
 * owns the template; spawnServe owns the run-scoped materialized copy. The key
 * already sits in the agent's plaintext `.env` (equivalent exposure); 0600.
 *
 * ENG-7956: but `opencode serve` initializes MCP servers ONLY from the GLOBAL
 * config it loads at boot (validated: the serve reads `~/.config/opencode/*` and
 * NEVER the project config for MCP; the CLI `opencode mcp list` DOES read the
 * project config, which is why it showed the server connected while the serve
 * had zero MCP tools). So we ALSO deliver the materialized `mcp` block into that
 * global config, or an opencode agent gets no MCP tools - and the manager's
 * stale-mcp health check then restart-loops it into the circuit breaker. Only the
 * `mcp` block goes global, so the project config keeps sole ownership of the
 * model/provider resolution (ENG-7931). opencode runs one agent per host today
 * (ADR-0047; serve HOME=/root), so the host-global config is effectively
 * per-agent; a multi-agent host would set a per-agent XDG_CONFIG_HOME, which
 * opencodeGlobalConfigPath already honours.
 */
function writeMaterializedOpencodeConfig(
  codeName: string,
  projectDir: string,
  serveEnv: NodeJS.ProcessEnv,
  log: (m: string) => void,
): boolean {
  const projectTarget = join(projectDir, MATERIALIZED_CONFIG_BASENAME);
  const globalTarget = opencodeGlobalConfigPath(serveEnv);
  let raw: string;
  try {
    raw = readFileSync(join(projectDir, 'opencode.json'), 'utf-8');
  } catch {
    // No provisioned template - drop any stale copies so the serve doesn't run on
    // an old config, then let it fall back to its own defaults. Not a failure.
    for (const t of [projectTarget, globalTarget]) {
      try { if (existsSync(t)) rmSync(t); } catch { /* best-effort */ }
    }
    return true;
  }
  const materialized = materializeEnvPlaceholders(raw, serveEnv);

  // (1) project sibling: session-level model/provider/permission (wins for model).
  // A write failure here means the serve would auth on the `{env:…}` template and
  // 401 every turn, so it's fatal (the caller refuses to launch).
  const projectOk = writeConfigFile600(projectTarget, materialized, codeName, log);

  // (2) GLOBAL config: the serve spawns MCP servers only from here at boot.
  const globalMcp = buildGlobalMcpConfig(materialized);
  let globalOk = true;
  if (globalMcp) {
    globalOk = writeConfigFile600(globalTarget, globalMcp, codeName, log);
  } else {
    // No MCP configured - drop any stale global copy from a prior spawn.
    try { if (existsSync(globalTarget)) rmSync(globalTarget); } catch { /* best-effort */ }
  }

  return projectOk && globalOk;
}

/** Base URL the manager uses to drive this agent's server. Loopback only. */
function baseUrlFor(port: number): string {
  return `http://127.0.0.1:${port}`;
}

/** Minimal Basic-auth header for readiness probes. */
function authHeader(password: string): Record<string, string> {
  const token = Buffer.from(`${SERVER_USERNAME}:${password}`).toString('base64');
  return { Authorization: `Basic ${token}` };
}

/**
 * Ask the OS for a free loopback port. There is a small TOCTOU window between
 * releasing it here and `opencode serve` binding it; acceptable for a
 * supervisor (a lost race surfaces as a failed readiness probe → respawn).
 */
function findFreePort(): Promise<number> {
  return new Promise((resolve, reject) => {
    const srv = createServer();
    srv.on('error', reject);
    srv.listen(0, '127.0.0.1', () => {
      const addr = srv.address();
      if (addr && typeof addr === 'object') {
        const { port } = addr;
        srv.close(() => resolve(port));
      } else {
        srv.close(() => reject(new Error('could not allocate a port')));
      }
    });
  });
}

/**
 * Start (or return the running) opencode server for an agent. Async because it
 * allocates a port before spawning. Mirrors `startPersistentSession`'s
 * backoff-on-repeated-crash contract.
 */
export async function startOpencodeSession(config: OpencodeSessionConfig): Promise<OpencodeSession> {
  const { codeName, log } = config;
  // ENG-7996: the inject path has no logger of its own; capture this one so a
  // turn-health warning can reach manager.log.
  loggers.set(codeName, log);

  const existing = sessions.get(codeName);

  // ENG-7995: reconcile the in-memory status against process reality BEFORE
  // trusting it. The early-return below is the only gate on respawning, and it
  // reads `status` alone — so a stop the manager did not initiate (an operator's
  // `tmux kill-session`, an OOM kill, the pane dying) desyncs the two
  // permanently: status stays 'running' pinned to the dead serve's port, this
  // returns before `spawnServe` ever runs, and `ensureOpencodeRuntime` then
  // reports `decision='spawn' spawnAttempted=true` against a port nothing is
  // listening on. Nothing else reconciles them, so the agent crash-loops until
  // the MANAGER is restarted (prod agent nora, 2026-07-22: ~10 minutes of
  // respawn attempts against dead port 33697; `systemctl restart agt-manager`
  // cleared the map and it came up cleanly on a fresh port).
  //
  // The dead port/password and the bridge cached against them are dropped here
  // rather than left to `spawnServe`, because the crash-backoff below can defer
  // the respawn by a cycle — and a bridge pinned to a dead (port, password) is
  // exactly the stale-state class ENG-7975 tracks for session continuity.
  //
  // `startedAt` is deliberately NOT reset: the backoff measures from the last
  // SUCCESSFUL spawn, so a long-lived serve that gets killed respawns
  // immediately, while one that dies right after spawning still backs off.
  if (existing && existing.status === 'running' && !isOpencodeSessionHealthy(codeName)) {
    log(
      `[opencode-session] '${codeName}' stopped out-of-band (tmux session gone, in-memory state pinned to port ${existing.port ?? '?'}) — respawning on a fresh port (ENG-7995)`,
    );
    existing.status = 'crashed';
    existing.restartCount++;
    existing.port = null;
    existing.password = null;
    bridges.delete(codeName);
    stopTranscriptRefresher(codeName);
  }

  if (existing && existing.status === 'running') return existing;

  // Backoff on repeated crashes (same shape as the Claude Code launcher).
  const restartCount = existing?.restartCount ?? 0;
  if (existing?.status === 'crashed' && existing.startedAt) {
    const backoffMs = Math.min(5000 * Math.pow(2, restartCount), 60_000);
    if (Date.now() - existing.startedAt < backoffMs) return existing;
  }

  if (config.isolated) {
    // ADR-0047: container-per-agent is the decision, but the container launcher
    // is not built. Refuse rather than run unisolated on a multi-tenant host.
    log(
      `[opencode-session] refusing to spawn '${codeName}': Docker isolation requested but the opencode container runtime is not implemented yet (ADR-0047). Not running unisolated on a multi-tenant host.`,
    );
    const blocked: OpencodeSession = {
      codeName,
      startedAt: Date.now(),
      restartCount: restartCount + 1,
      status: 'crashed',
      port: null,
      password: null,
      lastFailureTail: 'opencode isolation not implemented (ADR-0047)',
      model: null,
      projectDir: config.projectDir,
    };
    sessions.set(codeName, blocked);
    return blocked;
  }

  const session: OpencodeSession = {
    codeName,
    startedAt: null,
    restartCount,
    status: 'starting',
    port: null,
    password: null,
    lastFailureTail: existing?.lastFailureTail ?? null,
    // ENG-7931: parse the resolved model from the provisioned opencode.json
    // (config.projectDir IS the provision dir the serve reads) so the bridge can
    // pass it explicitly on session-create + prompt.
    model: readOpencodeModelFromConfigDir(config.projectDir),
    // ENG-8032: retained so getBridge can drive turns via `opencode run --dir`.
    projectDir: config.projectDir,
  };
  sessions.set(codeName, session);

  try {
    await spawnServe(config, session);
  } catch (err) {
    log(`[opencode-session] failed to start '${codeName}': ${(err as Error).message}`);
    session.status = 'crashed';
    session.startedAt = Date.now();
    session.restartCount++;
  }
  return session;
}

async function spawnServe(config: OpencodeSessionConfig, session: OpencodeSession): Promise<void> {
  const { codeName, projectDir, log } = config;

  if (!existsSync(join(projectDir, 'opencode.json'))) {
    // Not fatal to spawning, but a strong signal the provisioning step did not
    // run - the server would boot with no provider/MCP wiring.
    log(`[opencode-session] warning: no opencode.json in ${projectDir} for '${codeName}' (provisioning may not have run)`);
  }

  const tmuxSession = opencodeTmuxSession(codeName);
  const port = await findFreePort();
  const password = randomBytes(24).toString('base64url');

  // Clean slate: kill any stale server for this agent.
  try {
    execSync(`tmux kill-session -t ${tmuxSession} 2>/dev/null`, { stdio: 'ignore' });
  } catch { /* no existing session */ }

  mkdirSync(join(homedir(), '.augmented', codeName), { recursive: true });

  // Env: manager-supplied provider/AGT env + the server password + HOME/USER
  // backfill (parity with persistent-session's ENG-4632 defence: an SSM-launched
  // manager can lack HOME, which breaks config resolution).
  //
  // ENG-7883: source the agent's per-agent provider keys (`<agentDir>/.env`,
  // written by the opencode adapter's writeAuthProfiles from the auth profiles
  // /host/refresh delivers). opencode reads a provider's key from its process env
  // (e.g. XAI_API_KEY for a Grok agent), so without this a per-agent key never
  // reaches serve and it falls back to the shared host-level key. Layered AFTER
  // process.env so the per-agent key wins over a host key, and BEFORE
  // config.serveEnv so the manager's runtime env (AGT_*, OpenRouter) still wins.
  const serveEnv: NodeJS.ProcessEnv = {
    ...process.env,
    // ENG-7976: integration + channel credential secrets so the adapter's
    // `{env:VAR}` refs (remote MCP auth headers, native MCP env, channel tokens)
    // resolve at materialization. Layered after process.env (so the file supplies
    // creds absent from the host env) and before the per-agent provider key +
    // manager runtime env (which win for their own keys).
    ...readAgentIntegrationsEnv(codeName),
    ...readAgentProviderEnv(codeName),
    ...stripUndefined(config.serveEnv ?? {}),
    OPENCODE_SERVER_PASSWORD: password,
    HOME: (process.env.HOME?.trim()) || homedir(),
    USER: (process.env.USER?.trim()) || userInfo().username,
  };
  if (config.runId) serveEnv['AGT_RUN_ID'] = config.runId;
  if (config.agentTimezone) serveEnv['TZ'] = config.agentTimezone;

  // ENG-7931: write the materialized `opencode.jsonc` beside the provision
  // template so the serve (cwd = projectDir) auths on the real key/model instead
  // of the un-substituted `{env:…}` template - otherwise every turn 401s
  // "Incorrect API key" and the provider/model wiring never applies. If the write
  // fails there is no point launching the serve (it would auth on the template and
  // 401 forever), so refuse to spawn - startOpencodeSession's catch marks the
  // session crashed and the crash backoff retries next cycle.
  if (!writeMaterializedOpencodeConfig(codeName, projectDir, serveEnv, log)) {
    throw new Error('failed to write materialized opencode.jsonc (serve would 401 on the {env:…} template)');
  }

  // The command tmux runs. Secrets travel in the env (above), never on argv, so
  // they stay out of `ps`. `--print-logs` sends server logs to stdout, which the
  // pane log captures for post-crash diagnosis.
  const serveCmd = `${OPENCODE_BIN} serve --hostname 127.0.0.1 --port ${port} --print-logs`;

  log(`[opencode-session] starting '${tmuxSession}' for '${codeName}' on 127.0.0.1:${port}`);

  const child = spawn(
    'tmux',
    ['new-session', '-d', '-s', tmuxSession, '-c', projectDir, serveCmd],
    { cwd: projectDir, stdio: ['ignore', 'pipe', 'pipe'], env: serveEnv },
  );

  child.on('close', (code) => {
    if (code !== 0) {
      log(`[opencode-session] failed to create tmux session for '${codeName}' (exit ${code})`);
      session.status = 'crashed';
      session.startedAt = Date.now();
      session.restartCount++;
      return;
    }
    log(`[opencode-session] tmux session '${tmuxSession}' created for '${codeName}'`);
    setupPaneLog(tmuxSession, codeName, log);
  });

  child.on('error', (err) => {
    log(`[opencode-session] failed to start tmux for '${codeName}': ${err.message}`);
    session.status = 'crashed';
    session.startedAt = Date.now();
    session.restartCount++;
  });

  session.port = port;
  session.password = password;
  session.startedAt = Date.now();
  session.status = 'running';
  session.restartCount = 0;

  // A fresh server means any cached bridge (old port/password) is stale.
  bridges.delete(codeName);

  // ENG-7927: (re)start the Live View transcript refresher against the new
  // port/password. Restart rather than reuse - a prior timer would hold the old
  // credentials and every read would 401.
  stopTranscriptRefresher(codeName);
  startTranscriptRefresher(codeName);
}

/** Redirect the tmux pane to a log file so serve errors survive the process. */
function setupPaneLog(tmuxSession: string, codeName: string, log: (m: string) => void): void {
  const logPath = opencodePaneLogPath(codeName);
  try {
    execSync(`tmux pipe-pane -t ${tmuxSession} -o 'cat >> ${logPath}'`, { stdio: 'ignore' });
  } catch (err) {
    log(`[opencode-session] could not attach pane log for '${codeName}': ${(err as Error).message}`);
  }
}

/** Tail of the serve pane log, for surfacing a crash reason. */
export function readOpencodePaneLogTail(codeName: string, lines = 40): string | null {
  const logPath = opencodePaneLogPath(codeName);
  if (!existsSync(logPath)) return null;
  try {
    const all = readFileSync(logPath, 'utf8').split('\n');
    return all.slice(-lines).join('\n');
  } catch {
    return null;
  }
}

/** Process liveness: does the agent's tmux session still exist? */
export function isOpencodeSessionHealthy(codeName: string): boolean {
  const tmuxSession = opencodeTmuxSession(codeName);
  try {
    execSync(`tmux has-session -t ${tmuxSession} 2>/dev/null`, { stdio: 'ignore' });
    return true;
  } catch {
    return false;
  }
}

/**
 * Poll the HTTP surface until the server answers (it takes a beat to bind after
 * the process starts). Resolves true once reachable, false on timeout. A 401
 * still counts as "up" - the process is serving, only auth differs.
 */
export async function waitUntilOpencodeReady(
  codeName: string,
  opts: { timeoutMs?: number; pollIntervalMs?: number; attemptTimeoutMs?: number } = {},
): Promise<boolean> {
  const session = sessions.get(codeName);
  if (!session?.port || !session.password) return false;
  const timeoutMs = opts.timeoutMs ?? 20_000;
  const pollIntervalMs = opts.pollIntervalMs ?? 400;
  const attemptTimeoutMs = opts.attemptTimeoutMs ?? 2_000;
  const deadline = Date.now() + timeoutMs;
  const url = `${baseUrlFor(session.port)}/api/session`;
  const headers = authHeader(session.password);
  for (;;) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return false;
    try {
      // Any HTTP response (even 401) means the server is bound and serving.
      //
      // Each attempt is bounded by AbortSignal.timeout, capped by whatever is
      // left of the deadline. Without a signal an unanswered connect never
      // settles, so `await fetch` parks forever, the catch below never runs, and
      // the timeoutMs deadline is never evaluated - a wedged server (bound but
      // not responding, the normal shape while opencode is still loading) hangs
      // the caller instead of returning false. The per-attempt cap keeps polling
      // so a server that becomes healthy mid-window is still detected.
      await fetch(url, {
        method: 'GET',
        headers,
        signal: AbortSignal.timeout(Math.min(remaining, attemptTimeoutMs)),
      });
      return true;
    } catch {
      if (Date.now() >= deadline) return false;
      await sleep(pollIntervalMs);
    }
  }
}

/**
 * Inject a normalized inbound channel message into the agent's server and
 * (per the bridge's awaitReply) return the reply for the manager to send
 * outbound. This is the opencode analogue of `injectMessage` on the Claude
 * Code path.
 */
export async function injectOpencodeMessage(
  codeName: string,
  msg: InboundMessage,
  opts: { gate?: SenderGate; awaitReply?: boolean } = {},
): Promise<InboundResult> {
  const session = sessions.get(codeName);
  if (!session || session.status !== 'running' || !session.port || !session.password) {
    // NOT recorded as a turn outcome (ENG-7996): no turn was attempted, and
    // "the serve isn't running" is precisely what the process-liveness signal
    // already reports. Counting it would double-report a dead serve as a wedge.
    return { status: 'declined', reason: 'server_not_running' };
  }
  // gate + awaitReply are per-CALL: one cached bridge is reused across every
  // inbound for this agent (it owns the durable conversationKey → sessionID
  // map), so they must be passed to handleInbound, not baked in at build time —
  // otherwise a fire-and-forget nudge (awaitReply:false) that follows a
  // request/reply turn would inherit the first call's awaitReply and block.
  const bridge = getBridge(codeName, session.port, session.password);

  // ENG-7996: this is the ONE choke point every opencode turn passes through -
  // direct-chat, the Slack and Telegram ingests, and the scheduler all call it -
  // so recording the outcome here covers every lane from a single site, and a
  // channel added later is covered without touching this file. Each of those
  // callers already branches on the outcome for its own purposes and then
  // discards it; the tracker is what remembers.
  // ENG-8090: the agent is OCCUPIED from here until the turn settles. Reported
  // as an interval rather than a point because the busy sampler runs minutely:
  // a 5-minute turn stamped only at its end marks one bucket busy instead of
  // five. The matching `endTurn` runs in a `finally` so a throw cannot strand
  // the agent permanently "busy", but it is gated on this serve still being the
  // live one — see the note there.
  activityTracker.beginTurn(codeName);
  let occupied = true;
  try {
    const result = await bridge.handleInbound(msg, { gate: opts.gate, awaitReply: opts.awaitReply });
    // An `admitted` result (or a `replied` carrying empty text) is the wedge
    // shape ENG-8058 produced: the serve took the turn and never answered. The
    // manager's own callers treat exactly this as "produced no reply".
    const outcome: TurnOutcome =
      result.status === 'declined'
        ? 'declined'
        : result.status === 'replied' && result.reply
          ? 'replied'
          : opts.awaitReply === false
            ? 'admitted'
            : 'no_reply';
    // ENG-8090: a gate decline is not work — no model call happened. Its own
    // in-flight window is microseconds, but stamping it would hold the agent
    // "busy" for a further sampler window, manufacturing billable minutes for an
    // agent that only filtered a message.
    occupied = outcome !== 'declined';
    noteOpencodeTurnOutcome(codeName, outcome, session);
    return result;
  } catch (err) {
    // The inject threw (spawn failure, connection refused, run timeout). The
    // agent was asked to work and did not answer, whatever the cause.
    noteOpencodeTurnOutcome(codeName, 'failed', session);
    throw err;
  } finally {
    // ENG-8090: only the serve that STARTED this turn may end its occupancy.
    // A turn can outlive its serve — `opencode run` gets 180s and a teardown
    // (credential rotation ENG-8093, dashboard restart ENG-8000, model change)
    // can land mid-flight. Decrementing unconditionally would then subtract
    // from the REPLACEMENT generation's in-flight count, marking an agent idle
    // while its current turn is still running; and because `endTurn` creates
    // state on demand, a late settle after a stop-without-respawn would
    // resurrect a stopped agent as "busy just now". `stopOpencodeSession`
    // already reset this agent's occupancy, so there is nothing legitimate
    // left to decrement on either path. (CodeRabbit, PR #3745.)
    if (isLiveOpencodeSession(codeName, session)) {
      activityTracker.endTurn(codeName, occupied);
    }
  }
}

/**
 * Whether `startedOn` is still the live, running session for `codeName`.
 *
 * `startOpencodeSession` builds a NEW session object per spawn, so identity
 * catches a replacement; the status check catches a teardown with no respawn yet
 * (`stopOpencodeSession` mutates this same object to 'stopped').
 */
function isLiveOpencodeSession(codeName: string, startedOn: OpencodeSession): boolean {
  return sessions.get(codeName) === startedOn && startedOn.status === 'running';
}

/**
 * Record a turn outcome and emit one WARN per wedge streak. Kept beside the
 * injector rather than at the call sites so no lane can forget to report, and
 * so the log line reads the same regardless of which channel drove the turn.
 *
 * `startedOn` is the session the turn was actually dispatched against, captured
 * BEFORE the await. A turn can outlive its serve - `opencode run` is allowed
 * 180s and a teardown (model change, integration change, dashboard restart) can
 * land in the middle - and when it finally settles, recording it would attribute
 * a dead serve's outcome to whatever session now holds the slot. That would
 * corrupt the very counter this exists to make trustworthy: a fresh serve could
 * be born mid-streak, or a reset streak could be silently re-armed. So a result
 * is recorded only while its own serve is still the live, running one.
 * (CodeRabbit, PR #3722.)
 */
function noteOpencodeTurnOutcome(
  codeName: string,
  outcome: TurnOutcome,
  startedOn: OpencodeSession,
): void {
  // Shared with the occupancy guard in `injectOpencodeMessage`'s finally block,
  // so the two cannot drift apart — they are answering the same question.
  if (!isLiveOpencodeSession(codeName, startedOn)) return;

  const { health, shouldWarn, recovered } = turnOutcomeTracker.record(codeName, outcome);
  const log = loggers.get(codeName);
  if (!log) return;
  if (shouldWarn) {
    const lastOk = health.lastRepliedAt
      ? `${Math.round((Date.now() - health.lastRepliedAt) / 1000)}s ago`
      : 'never';
    // Probe the PROCESS, not the cached status. The cached value is exactly what
    // ENG-7995 showed can lie - it reads 'running' against a serve killed
    // out-of-band - and this line's whole job is to state whether the process is
    // up while its turns are not completing. (CodeRabbit, PR #3722.)
    log(
      `[turn-health] WARN: '${codeName}' has ${health.consecutiveFailures} consecutive turns with no reply ` +
      `(last successful turn: ${lastOk}; serve process alive=${isOpencodeSessionHealthy(codeName)}) — ` +
      `the serve is up but not completing turns (ENG-7996)`,
    );
  } else if (recovered) {
    log(`[turn-health] '${codeName}' completed a turn again after a no-reply streak (ENG-7996)`);
  }
}

/**
 * Get or (re)build the per-agent bridge for the current server instance. Keyed
 * on (port, password) so a server restart (new port/password) forces a fresh
 * bridge and drops the stale session map; the bridge is otherwise reused across
 * turns so a conversation keeps its opencode session. gate/awaitReply are NOT
 * bridge state — they are supplied per call to handleInbound.
 */
function getBridge(
  codeName: string,
  port: number,
  password: string,
): OpencodeInboundBridge {
  const cached = bridges.get(codeName);
  if (cached && cached.port === port && cached.password === password) return cached.bridge;
  const client = new HttpOpencodeClient({
    baseUrl: baseUrlFor(port),
    password,
    // A turn now runs inside one request, so the request timeout IS the turn
    // budget (see OPENCODE_RUN_TIMEOUT_MS). Leaving the client's 120s default
    // would quietly shorten it.
    requestTimeoutMs: OPENCODE_RUN_TIMEOUT_MS,
  });
  // ENG-7931: hand the bridge the resolved {providerID,id} so every session
  // create and turn runs the intended model. Parsed at spawn onto the session
  // state. (ENG-9096: the `/session` surface does apply opencode.json's default
  // model, unlike `/api` — this is now for determinism, not to avoid a silent
  // fallback to a free Zen model.)
  const model = sessions.get(codeName)?.model ?? null;
  // ENG-9096: turns go over HTTP again. ENG-8032 had to spawn `opencode run
  // --attach` per turn because the HTTP path delivered no MCP tools, but that
  // was specific to the `/api` surface; on `/session` the tools are present and
  // the reply comes back on the same call. No subprocess, no stdout parsing.
  const bridge = new OpencodeInboundBridge({
    client,
    sessionDefaults: model ? { model } : undefined,
  });
  bridges.set(codeName, { port, password, bridge });
  return bridge;
}

/** Stop the agent's server and forget its bridge. */
export function stopOpencodeSession(codeName: string, log: (m: string) => void): void {
  const tmuxSession = opencodeTmuxSession(codeName);
  try {
    execSync(`tmux kill-session -t ${tmuxSession} 2>/dev/null`, { stdio: 'ignore' });
    log(`[opencode-session] stopped '${tmuxSession}' for '${codeName}'`);
  } catch { /* already gone */ }
  stopTranscriptRefresher(codeName); // ENG-7927
  bridges.delete(codeName);
  // ENG-7996: a deliberate teardown ends this serve's turn history. Carrying the
  // streak across would have the fresh serve born already warned, and would let a
  // stale failure count outlive the condition that caused it.
  turnOutcomeTracker.reset(codeName);
  // ENG-8090: same reasoning for occupancy. A teardown also cannot leave a turn
  // running against this serve, so any surviving in-flight count is stale and
  // would otherwise report the (now stopped) agent as permanently busy.
  activityTracker.reset(codeName);
  // Drop the captured logger too, matching the lifecycle documented beside the
  // map - the next spawn re-registers it. (CodeRabbit, PR #3722.)
  loggers.delete(codeName);
  const session = sessions.get(codeName);
  if (session) {
    session.status = 'stopped';
    session.port = null;
    session.password = null;
  }
}

/** Current in-memory state for an agent's server, if any. */
export function getOpencodeSessionState(codeName: string): OpencodeSession | null {
  return sessions.get(codeName) ?? null;
}

/**
 * ENG-7996: the agent's turn-completion health — "are turns actually finishing",
 * as opposed to `isOpencodeSessionHealthy`'s "is the serve process up". Null
 * before any turn has been observed. Surfaced in the heartbeat's per-agent
 * diagnostics so a serve that is up but answering nothing is distinguishable
 * from a healthy one.
 */
export function getOpencodeTurnHealth(codeName: string): TurnHealth | null {
  return turnOutcomeTracker.get(codeName);
}

/**
 * ENG-8090: seconds since this agent was last doing work — 0 while a turn is in
 * flight — or null if no turn has been observed in this manager generation.
 *
 * This is the OCCUPANCY signal that feeds `agents.last_busy_activity_at` and so
 * the busy/idle sampler. It must never be routed into a liveness comparison:
 * a serve wedged mid-turn reports 0 here, which is correct for utilisation
 * (the agent was occupied) and catastrophic for liveness (it would mask exactly
 * the wedge class ENG-8058 produced). Liveness reads turn COMPLETION instead —
 * `getOpencodeTurnHealth().lastRepliedAt`.
 */
export function getOpencodeActivityAgeSeconds(
  codeName: string,
  now: number = Date.now(),
): number | null {
  return activityTracker.activityAgeSeconds(codeName, now);
}

/** Test seam: drop all turn-health state. */
export function __resetOpencodeTurnHealthForTests(): void {
  for (const codeName of sessions.keys()) {
    turnOutcomeTracker.reset(codeName);
    activityTracker.reset(codeName);
  }
}

/** Stop every supervised server (manager shutdown / restart hygiene). */
export function stopAllOpencodeSessions(log: (m: string) => void): void {
  for (const codeName of sessions.keys()) stopOpencodeSession(codeName, log);
}

// --- small helpers ---------------------------------------------------------

function stripUndefined(env: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(env)) if (v !== undefined) out[k] = v;
  return out;
}

/**
 * ENG-7883: parse the agent's per-agent provider-key file `<agentDir>/.env`
 * (written by the opencode adapter's writeAuthProfiles from the auth profiles
 * /host/refresh delivers - simple `<PROVIDER>_API_KEY=value` lines). Fail-safe: a
 * missing / unreadable file yields `{}` so an agent never fails to boot over it.
 * Exported for unit testing. `dir` overridable for tests; defaults to the
 * canonical `~/.augmented/<codeName>` agent dir.
 */
/**
 * Every key writeAuthProfiles emits is `<PROVIDER>_API_KEY` (the opencode
 * adapter's convention). readAgentProviderEnv forwards its result straight into
 * the `opencode serve` spawn env, so it allowlists ONLY that shape: a tampered
 * `.env` must not be able to inject arbitrary process env (PATH, LD_PRELOAD,
 * NODE_OPTIONS, ...) into the server. An unrecognised provider key is harmless
 * (opencode ignores it); anything not matching is dropped.
 */
const PROVIDER_KEY_RE = /^[A-Z][A-Z0-9_]*_API_KEY$/;

export function readAgentProviderEnv(codeName: string, dir?: string): Record<string, string> {
  const file = join(dir ?? join(homedir(), '.augmented', codeName), '.env');
  const out: Record<string, string> = {};
  try {
    if (!existsSync(file)) return out;
    for (const raw of readFileSync(file, 'utf-8').split('\n')) {
      const line = raw.trim();
      if (!line || line.startsWith('#')) continue;
      const eq = line.indexOf('=');
      if (eq <= 0) continue;
      const key = line.slice(0, eq).trim();
      // Deny-by-default: only provider API keys reach the spawn env.
      if (!PROVIDER_KEY_RE.test(key)) continue;
      let val = line.slice(eq + 1).trim();
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.slice(1, -1);
      }
      out[key] = val;
    }
  } catch {
    /* fail-safe: no per-agent provider env */
  }
  return out;
}

/**
 * Process-env keys that must NEVER be injectable from the on-disk
 * `.env.integrations` file, even though the manager is its only writer. A stray
 * or tampered entry for one of these would let the file redirect the serve's
 * loader / linker / shell instead of merely supplying a credential. Integration
 * + channel credential var names are open-ended (LINEAR_ACCESS_TOKEN,
 * MONDAY_API_KEY, SLACK_BOT_TOKEN, ...), so we deny-by-blocklist rather than
 * allowlist a single shape the way readAgentProviderEnv can.
 */
const INTEGRATIONS_ENV_BLOCKLIST = new Set([
  'PATH',
  'HOME',
  'USER',
  'SHELL',
  'IFS',
  'ENV',
  'BASH_ENV',
  'NODE_OPTIONS',
  'LD_PRELOAD',
  'LD_LIBRARY_PATH',
  'DYLD_INSERT_LIBRARIES',
  'DYLD_LIBRARY_PATH',
]);

/**
 * ENG-7976: parse the agent's `<agentDir>/.env.integrations` (raw `KEY=VALUE`
 * lines the opencode adapter's `upsertEnvIntegrations` / `writeChannelCredentials`
 * write) so the integration + channel credential secrets reach the `opencode
 * serve` spawn env. Without this the `{env:VAR}` refs the adapter renders into
 * `opencode.json` (e.g. a remote MCP's `Authorization: Bearer {env:LINEAR_ACCESS_TOKEN}`)
 * never resolve at materialization and the server ships an unusable literal
 * header. Fail-safe: a missing / unreadable file yields `{}` so an agent never
 * fails to boot over it. Exported for unit testing; `dir` overridable for tests.
 */
export function readAgentIntegrationsEnv(codeName: string, dir?: string): Record<string, string> {
  const file = join(dir ?? join(homedir(), '.augmented', codeName), '.env.integrations'); // agent-dir-allow: reads the agent's own .env.integrations by codename, parallel to the grandfathered readAgentProviderEnv; the opencode adapter writes this file under the codename agentDir
  const out: Record<string, string> = {};
  try {
    if (!existsSync(file)) return out;
    for (const raw of readFileSync(file, 'utf-8').split('\n')) {
      const line = raw.trim();
      if (!line || line.startsWith('#')) continue;
      const eq = line.indexOf('=');
      if (eq <= 0) continue;
      const key = line.slice(0, eq).trim();
      // Deny-by-default: the file supplies credentials, never process-env hijacks.
      if (INTEGRATIONS_ENV_BLOCKLIST.has(key)) continue;
      let val = line.slice(eq + 1).trim();
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.slice(1, -1);
      }
      out[key] = val;
    }
  } catch {
    /* fail-safe: no per-agent integration env */
  }
  return out;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
